export const FRIDGE_SUBSCRIPTION_OPERATION = "FridgeSubscription";
export const MAX_RETRY_ATTEMPTS = 3;

type RetryAttemptCallbacks = {
	onSubscriptionBroken: () => void;
	onDisconnected: () => void;
};

/**
 * Decides whether the AppSync RetryLink should retry, and reports connection state via callbacks.
 *
 * A FridgeSubscription error flips the app offline on the FIRST error rather than waiting for the
 * 3-attempt gate: each re-subscribe runs the AppSync handshake's own jitteredExponentialRetry, whose
 * WS-open can hang for minutes on a silent network — so the old gate could leave the error modal
 * hidden for >5 minutes. We still keep retrying in the background (return true until the limit) so the
 * subscription can recover, and still flip offline as a backstop when any operation exhausts its retries.
 */
export const resolveRetryAttempt = (
	count: number,
	operationName: string | undefined,
	error: unknown,
	{ onSubscriptionBroken, onDisconnected }: RetryAttemptCallbacks,
): boolean => {
	if (!error) {
		return false;
	}
	// A FridgeSubscription error flips offline on the FIRST error, but we keep retrying in the
	// background (until the limit) so the subscription can re-establish itself.
	if (operationName === FRIDGE_SUBSCRIPTION_OPERATION) {
		onSubscriptionBroken();
		onDisconnected();
		return count < MAX_RETRY_ATTEMPTS;
	}
	// Any other operation only flips offline once it has exhausted its retries.
	if (count >= MAX_RETRY_ATTEMPTS) {
		onDisconnected();
		return false;
	}
	return true;
};
